import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiPlus, FiTrash2, FiX, FiCheck, FiAlertCircle,
  FiEye, FiArchive, FiImage, FiChevronLeft, FiChevronRight,
} from "react-icons/fi";
import API from "../../utils/api";
import toast from "react-hot-toast";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";

/* ─────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────── */
const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const uid = () => Math.random().toString(36).slice(2, 9);

const STEPS = [
  { id: 1, label: "Basic" },
  { id: 2, label: "Images" },
  { id: 3, label: "Description" },
  { id: 4, label: "Specifications" },
  { id: 5, label: "Options" },
  { id: 6, label: "Variants" },
  { id: 7, label: "Pricing" },
  { id: 8, label: "SEO" },
  { id: 9, label: "Publish" },
];

/* ─────────────────────────────────────────────────────────────────────
   STEP INDICATOR (chevron style like /business/new-register)
───────────────────────────────────────────────────────────────────── */
function StepIndicator({ currentStep, totalSteps }) {
  return (
    <>
      <style>{`
        .pf-steps {
          display: flex;
          align-items: stretch;
          margin-bottom: 32px;
          gap: 0;
          overflow: hidden;
          border-radius: 6px;
          width: 100%;
        }
        .pf-step {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px 20px 10px 30px;
          background: #e2e8f0;
          color: #94a3b8;
          font-size: 0.72rem;
          font-weight: 600;
          white-space: nowrap;
          flex: 1;
          clip-path: polygon(0 0, calc(100% - 13px) 0, 100% 50%, calc(100% - 13px) 100%, 0 100%, 13px 50%);
          transition: background 0.2s, color 0.2s;
          cursor: default;
          min-width: 0;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .pf-step:first-child {
          clip-path: polygon(0 0, calc(100% - 13px) 0, 100% 50%, calc(100% - 13px) 100%, 0 100%);
          padding-left: 14px;
        }
        .pf-step:last-child {
          clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%, 13px 50%);
        }
        .pf-step.active {
          background: var(--primary, #3b82f6);
          color: #fff;
        }
        .pf-step.done {
          background: #bfdbfe;
          color: #1d4ed8;
        }
        @media (max-width: 700px) {
          .pf-step { font-size: 0.6rem; padding: 8px 12px 8px 22px; }
          .pf-step:first-child { padding-left: 10px; }
        }
      `}</style>
      <div className="pf-steps">
        {STEPS.map((s) => (
          <div
            key={s.id}
            className={`pf-step${s.id === currentStep ? " active" : s.id < currentStep ? " done" : ""}`}
          >
            {s.label}
          </div>
        ))}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   FIELD / LAYOUT HELPERS
───────────────────────────────────────────────────────────────────── */
function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, color: "#ef4444", fontSize: 12 }}>
      <FiAlertCircle size={12} /> {msg}
    </div>
  );
}

function StepTitle({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#111827" }}>{title}</h3>
      {subtitle && <p style={{ margin: "6px 0 0", fontSize: 13, color: "#6b7280" }}>{subtitle}</p>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   TIPTAP RICH EDITOR
───────────────────────────────────────────────────────────────────── */
function RichEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit, Link.configure({ openOnClick: false })],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  if (!editor) return null;

  const tb = (active, action, label) => (
    <button
      type="button"
      onClick={action}
      style={{
        padding: "3px 8px", fontSize: 12, border: "none", borderRadius: 4,
        cursor: "pointer", fontWeight: 600,
        background: active ? "#3b82f6" : "#f1f5f9",
        color: active ? "#fff" : "#374151",
      }}
    >{label}</button>
  );

  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 4, padding: "8px 10px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", flexWrap: "wrap" }}>
        {tb(editor.isActive("bold"),      () => editor.chain().focus().toggleBold().run(),       "B")}
        {tb(editor.isActive("italic"),    () => editor.chain().focus().toggleItalic().run(),     "I")}
        {tb(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), "H2")}
        {tb(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), "H3")}
        {tb(editor.isActive("bulletList"),  () => editor.chain().focus().toggleBulletList().run(),  "• List")}
        {tb(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), "1. List")}
        {tb(false, () => { const url = window.prompt("URL:"); if (url) editor.chain().focus().setLink({ href: url }).run(); }, "Link")}
        {tb(false, () => editor.chain().focus().unsetLink().run(), "Unlink")}
      </div>
      <EditorContent editor={editor} style={{ minHeight: 160, padding: "10px 12px", fontSize: 14, lineHeight: 1.7 }} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   IMAGE UPLOADER
───────────────────────────────────────────────────────────────────── */
function ImageUploader({ images, mainImage, onChange, onSetMain }) {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("image", file);
        const { data } = await API.post("/upload", fd);
        if (data.url) onChange("add", data.url);
      }
    } catch { toast.error("Image upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        {images.map((url, i) => (
          <div key={i} style={{
            position: "relative", border: url === mainImage ? "2.5px solid var(--primary, #3b82f6)" : "2px solid #e2e8f0",
            borderRadius: 8, overflow: "hidden", width: 100, height: 100,
          }}>
            <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            {url === mainImage && (
              <div style={{ position: "absolute", top: 4, left: 4, background: "var(--primary, #3b82f6)", color: "#fff", fontSize: 9, padding: "1px 6px", borderRadius: 3, fontWeight: 700 }}>MAIN</div>
            )}
            <div style={{ position: "absolute", top: 4, right: 4, display: "flex", flexDirection: "column", gap: 3 }}>
              <button onClick={() => onSetMain(url)} title="Set as main" style={imgActionBtn}><FiCheck size={9} color="#3b82f6" /></button>
              <button onClick={() => onChange("remove", url)} title="Remove" style={imgActionBtn}><FiX size={9} color="#ef4444" /></button>
            </div>
          </div>
        ))}
        <label style={{
          width: 100, height: 100, border: "2px dashed #e2e8f0", borderRadius: 8,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          cursor: "pointer", color: "#94a3b8", fontSize: 11, gap: 4,
        }}>
          <FiImage size={20} />
          {uploading ? "Uploading…" : "+ Add"}
          <input type="file" multiple accept="image/*" onChange={handleFiles} style={{ display: "none" }} />
        </label>
      </div>
      {images.length === 0 && (
        <p style={{ fontSize: 12, color: "#94a3b8", margin: 0 }}>Upload at least one product image. The first uploaded image becomes the main image.</p>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   STEP FOOTER (Save Draft + Prev/Next/Publish)
───────────────────────────────────────────────────────────────────── */
function StepFooter({ step, totalSteps, onPrev, onNext, onPublish, saving, isEdit, productId, navigate }) {
  const isLast = step === totalSteps;
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      marginTop: 36, paddingTop: 20, borderTop: "1px solid #e5e7eb", flexWrap: "wrap", gap: 10,
    }}>
      <div>
        {step > 1 && (
          <button type="button" onClick={onPrev} style={secBtn}>
            <FiChevronLeft size={14} /> Previous
          </button>
        )}
      </div>
      <div>
        {isLast ? null : (
          <button type="button" onClick={onNext} style={primaryBtn}>
            Next <FiChevronRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────────── */
export default function AdminProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id && id !== "add");

  const [step,    setStep]    = useState(1);
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(isEdit);

  /* Lookup data */
  const [categories,    setCategories]    = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [optionGroups,  setOptionGroups]  = useState([]);

  /* Step 1 — Basic */
  const [basic, setBasic] = useState({
    name: "", sku: "", categoryId: "", subcategoryId: "",
    status: "draft", isFeatured: false, displayOrder: 0, tags: "",
    hsnCode: "", gstPercent: "",   // optional
  });
  const [basicErrors, setBasicErrors] = useState({});

  /* Step 2 — Images */
  const [images,    setImages]    = useState([]);
  const [mainImage, setMainImage] = useState("");

  /* Step 3 — Description */
  const [shortDesc,    setShortDesc]    = useState("");
  const [detailedDesc, setDetailedDesc] = useState("");

  /* Step 4 — Specifications */
  const [specs, setSpecs] = useState([]);

  /* Step 5 — Options */
  const [productOptions,   setProductOptions]   = useState([]);
  const [showGroupPicker,  setShowGroupPicker]  = useState(false);
  const [newGroupName,     setNewGroupName]     = useState("");
  const [newGroupValues,   setNewGroupValues]   = useState("");

  /* Step 6 — Variants */
  const [variants,   setVariants]   = useState([]);
  const [generating, setGenerating] = useState(false);

  /* Step 8 — SEO */
  const [seo, setSeo] = useState({ slug: "", seoTitle: "", seoDescription: "" });
  const [seoErrors, setSeoErrors] = useState({});

  /* ── Load lookups ─────────────────────────────────────────────── */
  useEffect(() => {
    API.get("/catalog/categories").then(r => setCategories(r.data.categories || [])).catch(() => {});
    API.get("/catalog/option-groups").then(r => setOptionGroups(r.data.optionGroups || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!basic.categoryId) { setSubcategories([]); return; }
    API.get(`/catalog/subcategories?categoryId=${basic.categoryId}&status=active`)
      .then(r => setSubcategories(r.data.subcategories || []))
      .catch(() => {});
  }, [basic.categoryId]);

  /* ── Load product for edit ─────────────────────────────────────── */
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await API.get(`/catalog/products/${id}`);
        const p = data.product;
        setBasic({
          name: p.name, sku: p.sku, categoryId: String(p.categoryId),
          subcategoryId: p.subcategoryId ? String(p.subcategoryId) : "",
          status: p.status, isFeatured: p.isFeatured, displayOrder: p.displayOrder,
          tags: (p.tags || []).join(", "),
          hsnCode: p.hsnCode || "",
          gstPercent: p.gstPercent !== null && p.gstPercent !== undefined ? String(Number(p.gstPercent)) : "",
        });
        setImages(p.galleryImages || (p.mainImage ? [p.mainImage] : []));
        setMainImage(p.mainImage || "");
        setShortDesc(p.shortDescription || "");
        setDetailedDesc(p.detailedDescription || "");
        setSpecs((p.specifications || []).map(s => ({ ...s, id: s.id || uid() })));
        setProductOptions(p.productOptions || []);
        setSeo({ slug: p.slug || "", seoTitle: p.seoTitle || "", seoDescription: p.seoDescription || "" });
        setVariants((p.variants || []).map(v => ({ ...v, pricingDirty: false })));
      } catch { toast.error("Failed to load product"); }
      finally { setLoading(false); }
    })();
  }, [id, isEdit]);

  /* ── Image handlers ───────────────────────────────────────────── */
  const handleImages = (action, url) => {
    if (action === "add") {
      setImages(prev => {
        const next = [...prev, url];
        if (!mainImage) setMainImage(url);
        return next;
      });
    } else {
      setImages(prev => {
        const next = prev.filter(u => u !== url);
        if (mainImage === url) setMainImage(next[0] || "");
        return next;
      });
    }
  };

  /* ── Spec handlers ────────────────────────────────────────────── */
  const addSpec    = () => setSpecs(p => [...p, { id: uid(), label: "", value: "" }]);
  const removeSpec = (sid) => setSpecs(p => p.filter(s => s.id !== sid));
  const updateSpec = (sid, k, v) => setSpecs(p => p.map(s => s.id === sid ? { ...s, [k]: v } : s));

  /* ── Option handlers ──────────────────────────────────────────── */
  const addOptionGroup = (groupId) => {
    if (productOptions.some(o => o.optionGroupId === groupId)) { toast("Already added"); return; }
    setProductOptions(p => [...p, { optionGroupId: groupId, usedInVariants: true }]);
    setShowGroupPicker(false);
  };

  const createAndAddGroup = async () => {
    if (!newGroupName.trim()) { toast.error("Name required"); return; }
    const values = newGroupValues.split(",").map(v => v.trim()).filter(Boolean);
    try {
      const { data } = await API.post("/catalog/option-groups", { name: newGroupName, values });
      setOptionGroups(p => [...p, data.optionGroup]);
      addOptionGroup(data.optionGroup.id);
      setNewGroupName(""); setNewGroupValues("");
      toast.success("Option group created");
    } catch (e) { toast.error(e.response?.data?.message || "Error"); }
  };

  const toggleUsedInVariants = (groupId, val) =>
    setProductOptions(p => p.map(o => o.optionGroupId === groupId ? { ...o, usedInVariants: val } : o));

  const removeOption = (groupId) =>
    setProductOptions(p => p.filter(o => o.optionGroupId !== groupId));

  /* ── Variant generation ───────────────────────────────────────── */
  const handleGenerate = async () => {
    if (!isEdit) { toast.error("Save the product first before generating variants"); return; }
    const usedGroups = productOptions
      .filter(o => o.usedInVariants)
      .map(o => {
        const grp = optionGroups.find(g => g.id === o.optionGroupId);
        return grp ? { id: grp.id, name: grp.name, values: (grp.values || []).map(v => ({ id: v.id, value: v.value })) } : null;
      })
      .filter(Boolean);
    if (usedGroups.length === 0) { toast.error("No options marked as 'Used in Variants'"); return; }
    setGenerating(true);
    try {
      const { data } = await API.post(`/catalog/products/${id}/generate-variants`, { optionGroups: usedGroups });
      toast.success(`${data.added} variant(s) added`);
      const r = await API.get(`/catalog/products/${id}/variants`);
      setVariants(r.data.variants || []);
    } catch (e) { toast.error(e.response?.data?.message || "Error"); }
    finally { setGenerating(false); }
  };

  /* ── Variant update ───────────────────────────────────────────── */
  const updateVariantField = (variantId, k, v) =>
    setVariants(p => p.map(vr => vr.id === variantId ? { ...vr, [k]: v, dirty: true } : vr));

  const saveVariant = async (variant) => {
    try {
      await API.put(`/catalog/products/${id}/variants/${variant.id}`, {
        name: variant.name, sku: variant.sku, status: variant.status,
        minimumQuantity: variant.minimumQuantity, quantityIncrement: variant.quantityIncrement,
        displayOrder: variant.displayOrder,
      });
      setVariants(p => p.map(v => v.id === variant.id ? { ...v, dirty: false } : v));
      toast.success("Variant saved");
    } catch (e) { toast.error(e.response?.data?.message || "Error"); }
  };

  const archiveVariant = async (variantId) => {
    if (!window.confirm("Archive this variant?")) return;
    try {
      await API.patch(`/catalog/products/${id}/variants/${variantId}/archive`);
      setVariants(p => p.map(v => v.id === variantId ? { ...v, status: "archived" } : v));
      toast.success("Variant archived");
    } catch { toast.error("Failed"); }
  };

  /* ── Pricing ──────────────────────────────────────────────────── */
  const savePricing = async (variant) => {
    const tiers = (variant.pricing || []).filter(t => t.quantity && t.price);
    try {
      await API.put(`/catalog/products/${id}/variants/${variant.id}/pricing`, { tiers });
      setVariants(p => p.map(v => v.id === variant.id ? { ...v, pricingDirty: false } : v));
      toast.success("Pricing saved");
    } catch (e) { toast.error(e.response?.data?.message || "Error"); }
  };

  const updatePricingTier = (variantId, tierIdx, k, v) =>
    setVariants(p => p.map(vr => {
      if (vr.id !== variantId) return vr;
      const pricing = (vr.pricing || []).map((t, i) => i === tierIdx ? { ...t, [k]: v } : t);
      return { ...vr, pricing, pricingDirty: true };
    }));

  const addPricingTier = (variantId) =>
    setVariants(p => p.map(vr => {
      if (vr.id !== variantId) return vr;
      return { ...vr, pricing: [...(vr.pricing || []), { quantity: "", price: "" }], pricingDirty: true };
    }));

  const removePricingTier = (variantId, tierIdx) =>
    setVariants(p => p.map(vr => {
      if (vr.id !== variantId) return vr;
      return { ...vr, pricing: (vr.pricing || []).filter((_, i) => i !== tierIdx), pricingDirty: true };
    }));

  /* ── Step validation ──────────────────────────────────────────── */
  const validateStep1 = () => {
    const errs = {};
    if (!basic.name.trim())   errs.name = "Product Name is required";
    if (!basic.sku.trim())    errs.sku  = "SKU is required";
    if (!basic.categoryId)    errs.categoryId = "Category is required";
    // HSN is optional — validate only when filled
    const hsn = (basic.hsnCode || "").replace(/\s+/g, "");
    if (hsn && !/^\d{4}(\d{2})?(\d{2})?$/.test(hsn)) errs.hsnCode = "HSN must be 4, 6 or 8 digits";
    setBasicErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep8 = () => {
    const errs = {};
    if (!seo.slug.trim()) errs.slug = "Product Slug is required";
    setSeoErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /* ── Build save payload ───────────────────────────────────────── */
  const buildPayload = (publishStatus) => ({
    ...basic,
    hsnCode:    (basic.hsnCode || "").replace(/\s+/g, "") || null,
    gstPercent: basic.gstPercent === "" ? null : Number(basic.gstPercent),
    tags: basic.tags ? basic.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
    mainImage,
    galleryImages: images,
    shortDescription:    shortDesc,
    detailedDescription: detailedDesc,
    specifications:      specs,
    productOptions,
    ...seo,
    slug:   seo.slug || slugify(basic.name),
    status: publishStatus || basic.status,
  });

  /* ── Save ─────────────────────────────────────────────────────── */
  const save = async (publishStatus) => {
    if (!validateStep1()) { setStep(1); toast.error("Please fix errors on Step 1 first"); return; }
    if (!validateStep8()) { setStep(8); toast.error("Please fix errors on Step 8 first"); return; }

    setSaving(true);
    const payload = buildPayload(publishStatus);
    try {
      if (isEdit) {
        await API.put(`/catalog/products/${id}`, payload);
        toast.success("Product saved");
        navigate("/admin/products");
      } else {
        const { data } = await API.post("/catalog/products", payload);
        toast.success("Product created");
        navigate("/admin/products", { replace: true });
        return;
      }
    } catch (e) { toast.error(e.response?.data?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const saveDraft = () => save("draft");
  const publish   = () => save("active");

  /* ── Step navigation ──────────────────────────────────────────── */
  const goNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 8 && !validateStep8()) return;
    if (step < STEPS.length) setStep(s => s + 1);
  };

  const goPrev = () => { if (step > 1) setStep(s => s - 1); };

  /* ── Loading ──────────────────────────────────────────────────── */
  if (loading) return <div style={{ padding: 40, color: "#64748b" }}>Loading product…</div>;

  const footerProps = {
    step, totalSteps: STEPS.length, onPrev: goPrev, onNext: goNext,
    onPublish: publish,
  };

  return (
    <div style={{ padding: "28px 32px", width: "100%", boxSizing: "border-box" }}>
      {/* Page header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 10 }}>
        <div>
          <button onClick={() => navigate("/admin/products")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--primary, #3b82f6)", fontSize: 13, padding: 0, marginBottom: 6 }}>
            ← Back to Products
          </button>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{isEdit ? "Edit Product" : "Add Product"}</h2>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={saveDraft} disabled={saving} style={secBtn}>Save Draft</button>
          {isEdit && (
            <button onClick={() => navigate(`/admin/products/${id}/preview`)} style={secBtn}>
              <FiEye size={13} style={{ marginRight: 5 }} />Preview
            </button>
          )}
          <button onClick={publish} disabled={saving} style={primaryBtn}>
            {saving ? "Saving…" : "Publish"}
          </button>
        </div>
      </div>

      {/* Stepper */}
      <StepIndicator currentStep={step} totalSteps={STEPS.length} />

      {/* ── STEP 1: Basic Information ─────────────────────────────── */}
      {step === 1 && (
        <div>
          <StepTitle title="Basic Information" subtitle="Name, SKU, category, and visibility settings." />

          <div style={row}>
            <div style={col}>
              <label style={lbl}>Product Name *</label>
              <input
                style={{ ...inp, borderColor: basicErrors.name ? "#ef4444" : "#e2e8f0" }}
                value={basic.name}
                onChange={e => {
                  const name = e.target.value;
                  setBasic(b => ({ ...b, name }));
                  if (!isEdit || !seo.slug) setSeo(s => ({ ...s, slug: slugify(name) }));
                  if (basicErrors.name) setBasicErrors(er => ({ ...er, name: "" }));
                }}
                placeholder="e.g. Premium Visiting Card"
              />
              <FieldError msg={basicErrors.name} />
            </div>
            <div style={col}>
              <label style={lbl}>SKU *</label>
              <input
                style={{ ...inp, borderColor: basicErrors.sku ? "#ef4444" : "#e2e8f0" }}
                value={basic.sku}
                onChange={e => { setBasic(b => ({ ...b, sku: e.target.value })); if (basicErrors.sku) setBasicErrors(er => ({ ...er, sku: "" })); }}
                placeholder="e.g. VC-PREMIUM-001"
              />
              <FieldError msg={basicErrors.sku} />
            </div>
          </div>

          <div style={row}>
            <div style={col}>
              <label style={lbl}>Category *</label>
              <select
                style={{ ...inp, borderColor: basicErrors.categoryId ? "#ef4444" : "#e2e8f0" }}
                value={basic.categoryId}
                onChange={e => { setBasic(b => ({ ...b, categoryId: e.target.value, subcategoryId: "" })); if (basicErrors.categoryId) setBasicErrors(er => ({ ...er, categoryId: "" })); }}
              >
                <option value="">— Select Category —</option>
                {categories.filter(c => c.status === "active").map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <FieldError msg={basicErrors.categoryId} />
            </div>
            <div style={col}>
              <label style={lbl}>Subcategory</label>
              <select style={inp} value={basic.subcategoryId} onChange={e => setBasic(b => ({ ...b, subcategoryId: e.target.value }))} disabled={!basic.categoryId}>
                <option value="">— None —</option>
                {subcategories.filter(s => s.status === "active").map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={row}>
            <div style={col}>
              <label style={lbl}>HSN Code <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span></label>
              <input
                style={{ ...inp, borderColor: basicErrors.hsnCode ? "#ef4444" : "#e2e8f0" }}
                value={basic.hsnCode}
                maxLength={8}
                inputMode="numeric"
                onChange={e => { setBasic(b => ({ ...b, hsnCode: e.target.value.replace(/\D/g, "") })); if (basicErrors.hsnCode) setBasicErrors(er => ({ ...er, hsnCode: "" })); }}
                placeholder="e.g. 4911 (4, 6 or 8 digits)"
              />
              <FieldError msg={basicErrors.hsnCode} />
            </div>
            <div style={col}>
              <label style={lbl}>GST % <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span></label>
              <select style={inp} value={basic.gstPercent} onChange={e => setBasic(b => ({ ...b, gstPercent: e.target.value }))}>
                <option value="">— Not applicable —</option>
                {[...new Set([0, 3, 5, 12, 18, 28, 40, ...(basic.gstPercent !== "" ? [Number(basic.gstPercent)] : [])])]
                  .sort((a, b) => a - b)
                  .map(r => <option key={r} value={String(r)}>{r}%</option>)}
              </select>
            </div>
          </div>

          <div style={row}>
            <div style={col}>
              <label style={lbl}>Status</label>
              <select style={inp} value={basic.status} onChange={e => setBasic(b => ({ ...b, status: e.target.value }))}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div style={col}>
              <label style={lbl}>Display Order</label>
              <input type="number" style={inp} value={basic.displayOrder} onChange={e => setBasic(b => ({ ...b, displayOrder: parseInt(e.target.value) || 0 }))} />
            </div>
            <div style={{ ...col, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 500, paddingBottom: 3 }}>
                <input type="checkbox" checked={basic.isFeatured} onChange={e => setBasic(b => ({ ...b, isFeatured: e.target.checked }))} style={{ width: 15, height: 15 }} />
                Featured Product
              </label>
            </div>
          </div>

          <div>
            <label style={lbl}>Tags <span style={{ fontWeight: 400, color: "#94a3b8" }}>(comma separated)</span></label>
            <input style={inp} value={basic.tags} onChange={e => setBasic(b => ({ ...b, tags: e.target.value }))} placeholder="premium, corporate, wedding" />
          </div>

          <StepFooter {...footerProps} />
        </div>
      )}

      {/* ── STEP 2: Images ────────────────────────────────────────── */}
      {step === 2 && (
        <div>
          <StepTitle title="Product Images" subtitle="Upload product images. Click ✓ on any image to set it as the main image." />
          <ImageUploader images={images} mainImage={mainImage} onChange={handleImages} onSetMain={setMainImage} />
          <StepFooter {...footerProps} />
        </div>
      )}

      {/* ── STEP 3: Description ───────────────────────────────────── */}
      {step === 3 && (
        <div>
          <StepTitle title="Description" subtitle="Short description is shown in product cards. Detailed description appears on the product page." />

          <div style={{ marginBottom: 20 }}>
            <label style={lbl}>Short Description <span style={{ fontWeight: 400, color: "#94a3b8" }}>(used in product cards)</span></label>
            <textarea style={{ ...inp, minHeight: 80, resize: "vertical" }} value={shortDesc} onChange={e => setShortDesc(e.target.value)} maxLength={500} placeholder="One or two lines describing the product…" />
            <div style={{ textAlign: "right", fontSize: 11, color: "#94a3b8" }}>{shortDesc.length}/500</div>
          </div>

          <div>
            <label style={lbl}>Detailed Description <span style={{ fontWeight: 400, color: "#94a3b8" }}>(full product page)</span></label>
            <RichEditor value={detailedDesc} onChange={setDetailedDesc} />
          </div>

          <StepFooter {...footerProps} />
        </div>
      )}

      {/* ── STEP 4: Specifications ────────────────────────────────── */}
      {step === 4 && (
        <div>
          <StepTitle title="Specifications" subtitle="Informational key-value pairs shown on the product page. These do not generate variants." />

          {specs.map(spec => (
            <div key={spec.id} style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
              <input style={{ ...inp, flex: 1 }} value={spec.label} onChange={e => updateSpec(spec.id, "label", e.target.value)} placeholder="e.g. Paper" />
              <input style={{ ...inp, flex: 1 }} value={spec.value} onChange={e => updateSpec(spec.id, "value", e.target.value)} placeholder="e.g. 300 GSM" />
              <button onClick={() => removeSpec(spec.id)} style={iconBtn}><FiTrash2 size={13} color="#ef4444" /></button>
            </div>
          ))}

          <button onClick={addSpec} style={secBtn}><FiPlus size={13} style={{ marginRight: 5 }} />Add Specification</button>
          <StepFooter {...footerProps} />
        </div>
      )}

      {/* ── STEP 5: Options ───────────────────────────────────────── */}
      {step === 5 && (
        <div>
          <StepTitle title="Product Options" subtitle='Add option groups (e.g. Paper, Finish). Mark them as "Used in Variants" to include in variant generation.' />

          {productOptions.map(opt => {
            const grp = optionGroups.find(g => g.id === opt.optionGroupId);
            if (!grp) return null;
            return (
              <div key={opt.optionGroupId} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{grp.name}</div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>{(grp.values || []).map(v => v.value).join(" · ")}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <label style={{
                      fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                      padding: "4px 10px", borderRadius: 20,
                      background: opt.usedInVariants ? "#eff6ff" : "#f1f5f9",
                      color: opt.usedInVariants ? "#3b82f6" : "#64748b",
                      border: `1px solid ${opt.usedInVariants ? "#bfdbfe" : "#e2e8f0"}`,
                    }}>
                      <input type="checkbox" checked={opt.usedInVariants} onChange={e => toggleUsedInVariants(opt.optionGroupId, e.target.checked)} style={{ display: "none" }} />
                      {opt.usedInVariants ? "✓ Used in Variants" : "Not in Variants"}
                    </label>
                    <button onClick={() => removeOption(opt.optionGroupId)} style={iconBtn}><FiX size={13} color="#94a3b8" /></button>
                  </div>
                </div>
              </div>
            );
          })}

          {showGroupPicker ? (
            <div style={{ border: "1px solid #e2e8f0", borderRadius: 9, padding: 16, marginTop: 10 }}>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Select Existing Option Group</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {optionGroups.filter(g => !productOptions.some(o => o.optionGroupId === g.id)).map(g => (
                    <button key={g.id} onClick={() => addOptionGroup(g.id)} style={{ padding: "5px 12px", fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 20, cursor: "pointer", background: "#fff" }}>
                      {g.name}
                    </button>
                  ))}
                  {optionGroups.filter(g => !productOptions.some(o => o.optionGroupId === g.id)).length === 0 && (
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>All option groups already added.</span>
                  )}
                </div>
              </div>
              <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 14 }}>
                <label style={lbl}>Or Create New Option Group</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input style={{ ...inp, flex: "1 1 140px" }} value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Group name e.g. Paper" />
                  <input style={{ ...inp, flex: "2 1 220px" }} value={newGroupValues} onChange={e => setNewGroupValues(e.target.value)} placeholder="Values: 300 GSM, 350 GSM, 400 GSM" />
                  <button onClick={createAndAddGroup} style={primaryBtn}>Create & Add</button>
                </div>
              </div>
              <button onClick={() => setShowGroupPicker(false)} style={{ ...secBtn, marginTop: 10, fontSize: 12 }}>Cancel</button>
            </div>
          ) : (
            <button onClick={() => setShowGroupPicker(true)} style={{ ...secBtn, marginTop: productOptions.length > 0 ? 4 : 0 }}>
              <FiPlus size={13} style={{ marginRight: 5 }} />Add Option Group
            </button>
          )}

          <StepFooter {...footerProps} />
        </div>
      )}

      {/* ── STEP 6: Variants ──────────────────────────────────────── */}
      {step === 6 && (
        <div>
          <StepTitle title="Variants" subtitle="Generate and manage product variants from the options you defined." />

          {!isEdit && (
            <div style={{ background: "#fefce8", border: "1px solid #fde047", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#854d0e" }}>
              Save the product first (Save Draft), then come back to generate variants.
            </div>
          )}

          {isEdit && (
            <div style={{ marginBottom: 18 }}>
              <button onClick={handleGenerate} disabled={generating} style={{ ...primaryBtn, background: "#059669" }}>
                {generating ? "Generating…" : "⚡ Generate Variants from Options"}
              </button>
              <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 10 }}>Only options marked "Used in Variants" will be used.</span>
            </div>
          )}

          {variants.length === 0 && isEdit && (
            <p style={{ fontSize: 13, color: "#94a3b8" }}>No variants yet. Add options in Step 5 and click Generate.</p>
          )}

          {variants.map(v => (
            <div key={v.id} style={{ border: "1px solid #e2e8f0", borderRadius: 9, marginBottom: 12, overflow: "hidden", opacity: v.status === "archived" ? 0.55 : 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>{v.name}</span>
                <span style={{ fontSize: 11, background: v.status === "active" ? "#dcfce7" : v.status === "archived" ? "#f1f5f9" : "#fef9c3", color: v.status === "active" ? "#15803d" : v.status === "archived" ? "#94a3b8" : "#a16207", padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>{v.status}</span>
                {v.status !== "archived" && (
                  <button onClick={() => archiveVariant(v.id)} style={iconBtn} title="Archive"><FiArchive size={13} color="#f59e0b" /></button>
                )}
              </div>
              {v.status !== "archived" && (
                <div style={{ padding: "12px 14px" }}>
                  <div style={row}>
                    <div style={col}><label style={lbl}>Variant Name</label><input style={inp} value={v.name} onChange={e => updateVariantField(v.id, "name", e.target.value)} /></div>
                    <div style={col}><label style={lbl}>Variant SKU</label><input style={inp} value={v.sku} onChange={e => updateVariantField(v.id, "sku", e.target.value)} /></div>
                    <div style={{ width: 120 }}>
                      <label style={lbl}>Status</label>
                      <select style={inp} value={v.status} onChange={e => updateVariantField(v.id, "status", e.target.value)}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div style={row}>
                    <div style={col}><label style={lbl}>Min Quantity</label><input type="number" style={inp} value={v.minimumQuantity} onChange={e => updateVariantField(v.id, "minimumQuantity", parseInt(e.target.value) || 1)} min={1} /></div>
                    <div style={col}><label style={lbl}>Qty Increment</label><input type="number" style={inp} value={v.quantityIncrement} onChange={e => updateVariantField(v.id, "quantityIncrement", parseInt(e.target.value) || 1)} min={1} /></div>
                    <div style={{ ...col, display: "flex", alignItems: "flex-end" }}>
                      {v.dirty && <button onClick={() => saveVariant(v)} style={{ ...primaryBtn, fontSize: 12, padding: "7px 14px" }}>Save Variant</button>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          <StepFooter {...footerProps} />
        </div>
      )}

      {/* ── STEP 7: Quantity Pricing ──────────────────────────────── */}
      {step === 7 && (
        <div>
          <StepTitle title="Quantity Pricing" subtitle="Set pricing tiers per variant. Quantities must respect each variant's minimum and increment rules." />

          {variants.filter(v => v.status !== "archived").length === 0 && (
            <div style={{ background: "#fefce8", border: "1px solid #fde047", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#854d0e" }}>
              Go back to Step 6 and generate variants first.
            </div>
          )}

          {variants.filter(v => v.status !== "archived").map(v => (
            <div key={v.id} style={{ border: "1px solid #e2e8f0", borderRadius: 9, marginBottom: 16, overflow: "hidden" }}>
              <div style={{ padding: "10px 14px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontWeight: 600, fontSize: 13 }}>
                {v.name} <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 12 }}>— Min: {v.minimumQuantity}, Increment: {v.quantityIncrement}</span>
              </div>
              <div style={{ padding: "14px" }}>
                {(v.pricing || []).map((tier, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                    <div style={{ flex: 1 }}>
                      {i === 0 && <label style={lbl}>Quantity</label>}
                      <input type="number" style={inp} value={tier.quantity} placeholder="100" onChange={e => updatePricingTier(v.id, i, "quantity", e.target.value)} />
                    </div>
                    <div style={{ flex: 1 }}>
                      {i === 0 && <label style={lbl}>Price per piece (₹)</label>}
                      <input type="number" style={inp} value={tier.price} placeholder="2.50" onChange={e => updatePricingTier(v.id, i, "price", e.target.value)} />
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 1 }}>
                      <button onClick={() => removePricingTier(v.id, i)} style={iconBtn}><FiTrash2 size={13} color="#ef4444" /></button>
                    </div>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <button onClick={() => addPricingTier(v.id)} style={secBtn}><FiPlus size={12} style={{ marginRight: 4 }} />Add Tier</button>
                  {v.pricingDirty && <button onClick={() => savePricing(v)} style={primaryBtn}>Save Pricing</button>}
                </div>
              </div>
            </div>
          ))}

          <StepFooter {...footerProps} />
        </div>
      )}

      {/* ── STEP 8: SEO ───────────────────────────────────────────── */}
      {step === 8 && (
        <div>
          <StepTitle title="SEO" subtitle="Help customers find this product through search engines." />

          <div style={{ marginBottom: 16 }}>
            <label style={lbl}>Product Slug *</label>
            <input
              style={{ ...inp, borderColor: seoErrors.slug ? "#ef4444" : "#e2e8f0" }}
              value={seo.slug}
              onChange={e => { setSeo(s => ({ ...s, slug: slugify(e.target.value) })); if (seoErrors.slug) setSeoErrors(er => ({ ...er, slug: "" })); }}
              placeholder="e.g. premium-visiting-card"
            />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>URL: /category/subcategory/{seo.slug || "your-slug"}</div>
            <FieldError msg={seoErrors.slug} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={lbl}>SEO Title</label>
            <input style={inp} value={seo.seoTitle} onChange={e => setSeo(s => ({ ...s, seoTitle: e.target.value }))} placeholder="Premium Visiting Cards | Angel Printers" />
          </div>

          <div>
            <label style={lbl}>Meta Description</label>
            <textarea style={{ ...inp, minHeight: 80, resize: "vertical" }} value={seo.seoDescription} onChange={e => setSeo(s => ({ ...s, seoDescription: e.target.value }))} maxLength={160} placeholder="Premium quality visiting cards for businesses and professionals." />
            <div style={{ textAlign: "right", fontSize: 11, color: "#94a3b8" }}>{seo.seoDescription.length}/160</div>
          </div>

          <StepFooter {...footerProps} />
        </div>
      )}

      {/* ── STEP 9: Publish ───────────────────────────────────────── */}
      {step === 9 && (
        <div>
          <StepTitle title="Ready to Publish?" subtitle="Review your product and choose how to save it." />

          {/* Summary card */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "20px 24px", marginBottom: 28 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "14px 24px" }}>
              {[
                ["Product Name", basic.name || "—"],
                ["SKU", basic.sku || "—"],
                ["Status", basic.status],
                ["Category", categories.find(c => String(c.id) === String(basic.categoryId))?.name || "—"],
                ["HSN Code", basic.hsnCode || "—"],
                ["GST %", basic.gstPercent !== "" ? `${basic.gstPercent}%` : "—"],
                ["Images", `${images.length} uploaded`],
                ["Variants", `${variants.filter(v => v.status === "active").length} active`],
                ["Slug", seo.slug || "—"],
              ].map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280", marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", wordBreak: "break-all" }}>{value}</div>
                </div>
              ))}
            </div>
          </div>

          {isEdit && (
            <div style={{ marginTop: 8, paddingTop: 20, borderTop: "1px solid #f1f5f9" }}>
              <label style={lbl}>Change Status</label>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <select style={{ ...inp, width: 180 }} value={basic.status} onChange={e => setBasic(b => ({ ...b, status: e.target.value }))}>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="archived">Archived</option>
                </select>
                <button onClick={() => save(basic.status)} disabled={saving} style={primaryBtn}>Apply</button>
              </div>
            </div>
          )}

          {/* Only Previous button at the bottom — publish/save are in the top header */}
          <div style={{ marginTop: 36, paddingTop: 20, borderTop: "1px solid #e5e7eb" }}>
            <button type="button" onClick={goPrev} style={secBtn}>
              <FiChevronLeft size={14} /> Previous
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Shared styles ────────────────────────────────────────────────── */
const inp        = { width: "100%", padding: "9px 11px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, boxSizing: "border-box", outline: "none", background: "#fff" };
const lbl        = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 5 };
const row        = { display: "flex", gap: 14, marginBottom: 16, flexWrap: "wrap" };
const col        = { flex: 1, minWidth: 160 };
const primaryBtn = { display: "inline-flex", alignItems: "center", gap: 5, background: "var(--primary, #3b82f6)", color: "#fff", border: "none", borderRadius: 7, padding: "9px 18px", fontSize: 13, cursor: "pointer", fontWeight: 600 };
const secBtn     = { display: "inline-flex", alignItems: "center", gap: 5, background: "#f1f5f9", color: "#374151", border: "1px solid #e2e8f0", borderRadius: 7, padding: "9px 14px", fontSize: 13, cursor: "pointer", fontWeight: 500 };
const iconBtn    = { background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 5, display: "flex", alignItems: "center", borderRadius: 5 };
const imgActionBtn = { background: "#fff", border: "none", borderRadius: "50%", width: 20, height: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" };